import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ImportStep from './ImportStep';

const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;

// The parser rejects a report missing any of these, so the merchant has to be told all of them.
const REPORT_COLUMNS = [
  'Company Account',
  'Merchant Account',
  'Psp Reference',
  'Merchant Reference',
  'Modification Psp Reference',
  'Record Type',
  'Booking Date',
  'TimeZone',
  'Main Currency',
  'Main Amount',
  'Global Card Brand',
  'Card Number',
  'Acquirer Auth Code',
  'Acquirer Reference',
  'ARN',
  'Metadata',
];

const REPORT_EVENTS = ['Authorised', 'SentForSettle', 'SentForRefund', 'RefundedReversed'];

const INSTRUCTIONS_TEXT = [
  'How to generate the Adyen transaction report',
  '',
  '1. Customer Area → Reports → Interactive payment accounting',
  '2. On the "Column configuration" tab, tick exactly these columns, untick everything else, then Save Configuration:',
  `     ${REPORT_COLUMNS.join(', ')}`,
  '3. Select Generate, leave Timezone set to your account time zone, tick only these event types, and generate as .CSV:',
  `     ${REPORT_EVENTS.join(', ')}`,
  '4. Keep the date range to one month or less, and generate one file per month.',
  '5. Generating is not instant — a large account can take 30 minutes or more. Trigger the report, come back later, download it from Generated reports, and upload it into Chargemont.',
].join('\n');

function TickList({ items }: { items: string[] }) {
  return (
    <Box
      sx={{
        mt: 0.75,
        px: 1,
        py: 0.75,
        borderRadius: 1,
        backgroundColor: '#f5f5f7',
        fontFamily: 'monospace',
        fontSize: '0.78rem',
        lineHeight: 1.7,
        wordBreak: 'break-word',
      }}
    >
      {items.join(', ')}
    </Box>
  );
}

export default function AdyenPaymentImportDialog({
  accountId,
  accountName,
  onImport,
  onClose,
}: {
  accountId: number;
  accountName: string;
  onImport: (accountId: number, file: File) => Promise<void>;
  onClose: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCopyInstructions = async () => {
    await navigator.clipboard.writeText(INSTRUCTIONS_TEXT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePick = (picked: File | null) => {
    if (picked && picked.size > MAX_FILE_SIZE_BYTES) {
      setFile(null);
      setSizeError(
        `${picked.name} is ${(picked.size / 1024 / 1024).toFixed(0)} MB, over the 100 MB limit. Generate the report for a shorter date range, or with only the columns and events listed above.`,
      );
      return;
    }
    setSizeError(null);
    setFile(picked);
  };

  const handleImport = async () => {
    if (!file) return;
    setBusy(true);
    const imported = await onImport(accountId, file).then(() => true, () => false);
    setBusy(false);
    if (imported) {
      onClose();
    }
  };

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || busy) return;
        onClose();
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box component="span">Import Adyen transaction report — {accountName}</Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            copied
              ? <CheckIcon fontSize="small" sx={{ color: 'success.main' }} />
              : <ContentCopyIcon fontSize="small" />
          }
          onClick={handleCopyInstructions}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {copied ? 'Copied!' : 'Copy Instructions'}
        </Button>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Import historical transactions by uploading a payment report from the Adyen Customer Area.
        </DialogContentText>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2.5 }}>
          <ImportStep n={1}>
            In <strong>Reports</strong>, open <strong>Interactive payment accounting</strong>.
          </ImportStep>
          <ImportStep n={2}>
            On the <strong>Column configuration</strong> tab, tick exactly these columns, untick
            everything else, then <strong>Save Configuration</strong>. Leaving the rest unticked
            keeps the download small. A report missing a column we read is rejected, and the error
            names it.
            <TickList items={REPORT_COLUMNS} />
          </ImportStep>
          <ImportStep n={3}>
            Select <strong>Generate</strong>, leave <strong>Timezone</strong> set to your account
            time zone — the report records which zone it used — then tick only these event types
            and generate as <strong>.CSV</strong>.
            <TickList items={REPORT_EVENTS} />
          </ImportStep>
          <ImportStep n={4}>
            Keep the <strong>Date range</strong> to <strong>one month or less</strong> — longer
            ranges risk exceeding the 100 MB upload limit. Generate one file per month and upload
            them one at a time.
          </ImportStep>
          <ImportStep n={5}>
            Generating is not instant — on a large account it can take <strong>30 minutes or
            more</strong>. Trigger the report, come back later, download it from{' '}
            <strong>Generated reports</strong>, and upload it below.
          </ImportStep>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Transactions already in the system are merged rather than duplicated, so overlapping
          reports are safe to upload in any order.
        </Typography>

        {sizeError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {sizeError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            component="label"
            variant="outlined"
            disabled={busy}
            startIcon={<UploadFileIcon />}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Choose CSV
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => {
                handlePick(e.target.files?.[0] ?? null);
                e.target.value = '';
              }}
            />
          </Button>
          <Typography
            variant="body2"
            color={file ? 'text.primary' : 'text.secondary'}
            title={file?.name}
            sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {file ? file.name : 'No file selected'}
          </Typography>
        </Box>

        {busy && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            A large report takes several minutes to upload and import. Keep this tab open.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!file || busy}
          startIcon={busy ? <CircularProgress size={14} /> : undefined}
        >
          {busy ? 'Importing…' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
